<script lang="ts" module>
  // Notice — the design-system inline-callout primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). An inline
  // paragraph callout for abort / error / quota / informational / success
  // messages: a hairline box with a left semantic rule whose colour is keyed to
  // the tone. Error/warning/success use the theme-independent semantic tokens
  // (recording-red for error, leaf-green for success); info uses the practice
  // `--accent`. Carries `role="alert"` for error/warning so the message is
  // announced. No bespoke per-page notice chrome.
  //
  // Forwards arbitrary attributes (`data-testid`, `title`, `aria-*`, …) onto the
  // root via `...rest`, so a host never has to wrap the Notice in a `<div>` just
  // to attach a test hook or an aria reference.
  export type NoticeTone = 'error' | 'warning' | 'info' | 'success';
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { HTMLAttributes } from 'svelte/elements';

  interface Props extends HTMLAttributes<HTMLParagraphElement> {
    /** Semantic tone — drives the left-rule colour + the alert role. */
    tone: NoticeTone;
    /**
     * ARIA role. Defaults to `alert` for error/warning (announced) and
     * `status` for info/success. Override when the host already owns the live
     * region.
     */
    role?: 'alert' | 'status' | null;
    class?: string;
    /** The message body. */
    children: Snippet;
  }

  // The rest element forwards arbitrary host attributes; this primitive is
  // never compiled as a custom element, so the custom-element prop-inference
  // warning does not apply.
  let {
    tone,
    role = tone === 'error' || tone === 'warning' ? 'alert' : 'status',
    class: klass = '',
    children,
    ...rest
  }: Props = $props();
</script>

<p class="vd-notice vd-notice--{tone} {klass}" role={role ?? undefined} {...rest}>
  {@render children()}
</p>

<style lang="scss">
  .vd-notice {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--text-body);
    line-height: var(--text-body--line-height);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 0.6rem 0.85rem;
  }

  // Error / warning are semantic (theme-independent), NOT the practice accent.
  .vd-notice--error {
    border-left: 3px solid var(--color-error);
  }

  .vd-notice--warning {
    border-left: 3px solid var(--color-warning);
  }

  // Info takes the practice accent — it is an in-context prompt, not a fault.
  .vd-notice--info {
    border-left: 3px solid var(--accent);
  }

  // Success is semantic leaf-green (theme-independent) — a confirmation, not the
  // practice accent.
  .vd-notice--success {
    border-left: 3px solid var(--color-success);
  }
</style>
